/**
 * HTML Sanitization Utility
 * Provides XSS protection using DOMPurify with different configurations
 * for various content types (basic HTML, rich email HTML, SVG)
 */

import DOMPurify from 'dompurify';

/**
 * Basic HTML sanitization for chat messages
 * Allows: a, br, strong, em, b, i, p, span, ul, ol, li, code, pre
 */
export const sanitizeBasicHtml = (dirty) => {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      'a', 'br', 'strong', 'em', 'b', 'i', 'p', 'span',
      'ul', 'ol', 'li', 'code', 'pre', 'blockquote'
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    // Force all links to open in new tab safely
    ADD_ATTR: ['target', 'rel'],
    FORBID_TAGS: ['script', 'style', 'iframe', 'form', 'input'],
    // Hook to add security attributes to links
    AFTER_SANITIZE_ATTRIBUTES: function(node) {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }
  });
};

/**
 * Rich HTML sanitization for email content
 * Allows most HTML tags needed for email rendering including tables and inline styles
 */
export const sanitizeEmailHtml = (dirty) => {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    ALLOWED_TAGS: [
      // Text formatting
      'a', 'br', 'strong', 'em', 'b', 'i', 'u', 'p', 'span', 'div',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'blockquote', 'pre', 'code',
      // Tables (common in emails)
      'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
      // Media
      'img',
      // Structure
      'hr', 'center', 'font', 'sub', 'sup', 'small', 'big',
      // For outlook/email compatibility
      'address', 'article', 'aside', 'footer', 'header', 'main', 'nav', 'section'
    ],
    ALLOWED_ATTR: [
      // Common attributes
      'href', 'src', 'alt', 'title', 'class', 'id',
      // Styling (important for email rendering)
      'style', 'width', 'height', 'align', 'valign', 'bgcolor', 'color',
      'border', 'cellpadding', 'cellspacing', 'colspan', 'rowspan',
      // Links
      'target', 'rel',
      // Font (legacy but used in emails)
      'face', 'size'
    ],
    // Allow data URIs for images (common in emails)
    ALLOW_DATA_ATTR: false,
    ADD_URI_SAFE_ATTR: ['src'],
    FORBID_TAGS: ['script', 'iframe', 'form', 'input', 'button', 'textarea', 'select', 'object', 'embed'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    // Hook to add security attributes to links
    AFTER_SANITIZE_ATTRIBUTES: function(node) {
      if (node.tagName === 'A') {
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
      // Remove javascript: URLs
      if (node.hasAttribute('href')) {
        const href = node.getAttribute('href');
        if (href && href.toLowerCase().startsWith('javascript:')) {
          node.removeAttribute('href');
        }
      }
      if (node.hasAttribute('src')) {
        const src = node.getAttribute('src');
        if (src && src.toLowerCase().startsWith('javascript:')) {
          node.removeAttribute('src');
        }
      }
    }
  });
};

/**
 * SVG sanitization for flowcharts and diagrams
 * Allows SVG elements while removing potentially dangerous attributes
 */
export const sanitizeSvg = (dirty) => {
  if (!dirty) return '';

  return DOMPurify.sanitize(dirty, {
    USE_PROFILES: { svg: true, svgFilters: true },
    ADD_TAGS: ['use'],
    ALLOWED_TAGS: [
      // SVG root and structure
      'svg', 'g', 'defs', 'symbol', 'use', 'title', 'desc',
      // Shapes
      'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon', 'path',
      // Text
      'text', 'tspan', 'textPath',
      // Containers and references
      'clipPath', 'mask', 'pattern', 'marker',
      // Gradients and filters
      'linearGradient', 'radialGradient', 'stop',
      'filter', 'feBlend', 'feColorMatrix', 'feComponentTransfer',
      'feComposite', 'feConvolveMatrix', 'feDiffuseLighting',
      'feDisplacementMap', 'feFlood', 'feGaussianBlur', 'feImage',
      'feMerge', 'feMergeNode', 'feMorphology', 'feOffset',
      'feSpecularLighting', 'feTile', 'feTurbulence',
      // Font (for text in SVG)
      'font', 'font-face', 'glyph', 'missing-glyph'
    ],
    ALLOWED_ATTR: [
      // Common SVG attributes
      'id', 'class', 'style', 'transform', 'opacity',
      // Positioning
      'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
      'width', 'height', 'viewBox', 'preserveAspectRatio',
      // Path data
      'd', 'points', 'pathLength',
      // Stroke and fill
      'fill', 'fill-opacity', 'fill-rule', 'stroke', 'stroke-width',
      'stroke-linecap', 'stroke-linejoin', 'stroke-dasharray',
      'stroke-dashoffset', 'stroke-opacity', 'stroke-miterlimit',
      // Text attributes
      'font-family', 'font-size', 'font-weight', 'font-style',
      'text-anchor', 'dominant-baseline', 'alignment-baseline',
      'dx', 'dy', 'textLength', 'lengthAdjust',
      // References
      'href', 'xlink:href', 'clip-path', 'mask', 'filter', 'marker-start',
      'marker-mid', 'marker-end',
      // Gradient/filter specific
      'gradientUnits', 'gradientTransform', 'spreadMethod',
      'offset', 'stop-color', 'stop-opacity',
      'filterUnits', 'primitiveUnits', 'in', 'in2', 'result',
      'stdDeviation', 'mode', 'type', 'values',
      // Visibility
      'visibility', 'display', 'overflow',
      // Other
      'version', 'xmlns', 'xmlns:xlink', 'xml:space'
    ],
    FORBID_TAGS: ['script', 'foreignObject'],
    FORBID_ATTR: ['onload', 'onerror', 'onclick', 'onmouseover', 'onfocus']
  });
};

/**
 * Default export with all sanitizers
 */
export default {
  basic: sanitizeBasicHtml,
  email: sanitizeEmailHtml,
  svg: sanitizeSvg
};
